/**
 * Format-only regex: up to 3 digits optionally followed by '.' or ',' and up to 2 more digits.
 * Accepts both '.' and ',' as decimal separators to support locale-specific mobile keyboards.
 * Callers normalise ',' → '.' before parsing (same as Flutter's replaceAll(',', '.')).
 * Mirrors Flutter's FilteringTextInputFormatter.allow regex from content_monetization_screen.
 */
const DECIMAL_AMOUNT_FORMAT_REGEX = /^\d{0,3}([.,]\d{0,2})?$/;

// Literal partials Flutter special-cases so the user can start typing from an empty field.
const DECIMAL_AMOUNT_PARTIALS = new Set(['', '.', ',', '0.', '0,']);

export const TextInputFormatters = {
  /**
   * Returns the value unchanged if it passes format validation and the parsed number
   * is within [minValue, maxValue]. Returns previousValue for any invalid input so
   * the field stays unchanged. Allows Flutter's literal partial states ("", ".", ",",
   * "0.", "0,") so the user can start typing a decimal from scratch.
   */
  decimalAmount: (value: string, previousValue: string, minValue: number, maxValue: number): string => {
    if (!DECIMAL_AMOUNT_FORMAT_REGEX.test(value)) return previousValue;
    if (DECIMAL_AMOUNT_PARTIALS.has(value)) return value;
    const parsed = parseFloat(value.replace(',', '.'));
    if (isNaN(parsed) || parsed < minValue || parsed > maxValue) return previousValue;
    return value;
  },

  /**
   * Strict validation for form submission: value must be a fully parseable number
   * within [minValue, maxValue]. Intermediate states like "1." are rejected.
   */
  isValidDecimalAmount: (value: string, minValue: number, maxValue: number): boolean => {
    const parsed = parseFloat(value.replace(',', '.'));
    return !isNaN(parsed) && parsed >= minValue && parsed <= maxValue;
  },
} as const;
